import React from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import { FolderType } from '@/api/generated.ts';
import CreateProcess from '@/containers/CreateProcess/CreateProcess.tsx';
import Login from '@/containers/Login/Login.tsx';
import Onboarding from '@/containers/Onboarding/Onboarding.tsx';
import Organization from '@/containers/Organization/Organization.tsx';
import OrganizationItems from '@/containers/Organization/OrganizationItems.tsx';
import OrganizationProcesses from '@/containers/Organization/OrganizationProcesses.tsx';
import OrganizationTalents from '@/containers/Organization/OrganizationTalents.tsx';
import FolderBasicInfoTab from '@/containers/OrganizationItemSettings/FolderBasicInfoTab.tsx';
import FolderSettings from '@/containers/OrganizationItemSettings/FolderSettings.tsx';
import ItemNormsTab from '@/containers/OrganizationItemSettings/NormsTab.tsx';
import ItemProcessSettingsTab from '@/containers/OrganizationItemSettings/ProcessSettingsTab.tsx';
import ItemRolesUsersTab from '@/containers/OrganizationItemSettings/RolesUsersTab.tsx';
import SubFolderSettings from '@/containers/OrganizationItemSettings/SubFolderSettings.tsx';
import SubOrganizationSettings from '@/containers/OrganizationItemSettings/SubOrganizationSettings.tsx';
import SubOrgBasicInfoTab from '@/containers/OrganizationItemSettings/SubOrgBasicInfoTab.tsx';
import Organizations from '@/containers/Organizations/Organizations';
import BasicInfoTab from '@/containers/OrganizationSettings/BasicInfoTab.tsx';
import IdentificationNormsTab from '@/containers/OrganizationSettings/IdentificationNormsTab.tsx';
import OrganizationSettings from '@/containers/OrganizationSettings/OrganizationSettings';
import ProcessSettingsTab from '@/containers/OrganizationSettings/ProcessSettingsTab.tsx';
import RolesUsersTab from '@/containers/OrganizationSettings/RolesUsersTab.tsx';
import PodiumApp from '@/containers/PodiumApp/PodiumApp';
import Process from '@/containers/Process/Process.tsx';
import Processes from '@/containers/Processes/Processes.tsx';
import Roles from '@/containers/Roles/Roles.tsx';
import Talent from '@/containers/Talent/Talent.tsx';
import TalentTestSession from '@/containers/Talent/TalentTestSession/TalentTestSession.tsx';
import Talents from '@/containers/Talents/Talents.tsx';
import TalentsCreate from '@/containers/TalentsCreate.tsx';
import ClientUsers from '@/containers/Users/ClientUsers.tsx';
import MidotUsers from '@/containers/Users/MidotUsers.tsx';
import Users from '@/containers/Users/Users.tsx';
import UserSettings from '@/containers/Users/UserSettings.tsx';
import UserSettingsAccessRoles from '@/containers/Users/UserSettingsAccessRoles.tsx';
import UserSettingsBasicInfoTab from '@/containers/Users/UserSettingsBasicInfoTab.tsx';
import ProcessSettingsForm from '@/views/ProcessSettingsForm/ProcessSettingsForm.tsx';
import TalentRegistrationForm from '@/views/TalentRegistrationForm/TalentRegistrationForm.tsx';

import { ROUTER_PATHS } from './linker.ts';

const routes: RouteObject[] = [
  {
    path: '*',
    Component: () => <Navigate to={ROUTER_PATHS.HOME} />
  },
  {
    Component: () => (
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <Login />
      </QueryParamProvider>
    ),
    path: ROUTER_PATHS.LOGIN
  },
  {
    Component: () => (
      <QueryParamProvider adapter={ReactRouter6Adapter}>
        <Onboarding />
      </QueryParamProvider>
    ),
    path: ROUTER_PATHS.ONBOARDING
  },
  {
    Component: () => <PodiumApp />,
    path: ROUTER_PATHS.HOME,
    children: [
      {
        Component: () => <Organizations />,
        path: ROUTER_PATHS.ORGANIZATIONS
      },
      {
        Component: () => <Organization />,
        path: ROUTER_PATHS.ORGANIZATION,
        children: [
          {
            Component: () => <OrganizationItems />,
            path: ROUTER_PATHS.ORGANIZATION_ITEMS
          },
          {
            Component: () => <OrganizationProcesses />,
            path: ROUTER_PATHS.ORGANIZATION_PROCESSES
          },
          {
            Component: () => <OrganizationTalents />,
            path: ROUTER_PATHS.ORGANIZATION_TALENTS
          },
          {
            Component: () => null,
            path: ROUTER_PATHS.ORGANIZATION_HISTORY
          },
          {
            Component: () => <OrganizationSettings />,
            path: ROUTER_PATHS.ORGANIZATION_SETTINGS,
            children: [
              {
                Component: () => <BasicInfoTab />,
                path: ROUTER_PATHS.ORGANIZATION_SETTINGS_BASIC_INFO
              },
              {
                Component: () => <RolesUsersTab />,
                path: ROUTER_PATHS.ORGANIZATION_SETTINGS_ROLES_AND_MEMBERS
              },
              {
                Component: () => <ProcessSettingsTab />,
                path: ROUTER_PATHS.ORGANIZATION_SETTINGS_PROCESS_SETTINGS,
                children: [
                  {
                    Component: () => (
                      <TalentRegistrationForm
                        parentRoutePattern={ROUTER_PATHS.ORGANIZATION_SETTINGS_PROCESS_SETTINGS}
                      />
                    ),
                    path: ROUTER_PATHS.ORGANIZATION_SETTINGS_TALENT_REGISTRATION_FORM
                  }
                ]
              },
              {
                Component: () => <IdentificationNormsTab />,
                path: ROUTER_PATHS.ORGANIZATION_SETTINGS_IDENTIFICATION_AND_NORMS
              }
            ]
          },
          {
            Component: () => <SubOrganizationSettings />,
            path: ROUTER_PATHS.SUBORGANIZATION_SETTINGS,
            children: [
              {
                Component: () => <SubOrgBasicInfoTab />,
                path: ROUTER_PATHS.SUBORGANIZATION_SETTINGS_BASIC_INFO
              },
              {
                Component: () => <ItemRolesUsersTab />,
                path: ROUTER_PATHS.SUBORGANIZATION_SETTINGS_ROLES_AND_MEMBERS
              },
              {
                Component: () => <ItemProcessSettingsTab />,
                path: ROUTER_PATHS.SUBORGANIZATION_SETTINGS_PROCESS_SETTINGS,
                children: [
                  {
                    Component: () => (
                      <TalentRegistrationForm
                        parentRoutePattern={ROUTER_PATHS.SUBORGANIZATION_SETTINGS_PROCESS_SETTINGS}
                      />
                    ),
                    path: ROUTER_PATHS.SUBORGANIZATION_SETTINGS_TALENT_REGISTRATION_FORM
                  }
                ]
              },
              {
                Component: () => <ItemNormsTab />,
                path: ROUTER_PATHS.SUBORGANIZATION_SETTINGS_NORMS
              }
            ]
          },
          {
            Component: () => <FolderSettings />,
            path: ROUTER_PATHS.FOLDER_SETTINGS,
            children: [
              {
                Component: () => <FolderBasicInfoTab type={FolderType.FOLDER} />,
                path: ROUTER_PATHS.FOLDER_SETTINGS_BASIC_INFO
              },
              {
                Component: () => <ItemRolesUsersTab />,
                path: ROUTER_PATHS.FOLDER_SETTINGS_ROLES_AND_MEMBERS
              }
            ]
          },
          {
            Component: () => <SubFolderSettings />,
            path: ROUTER_PATHS.SUBFOLDER_SETTINGS,
            children: [
              {
                Component: () => <FolderBasicInfoTab type={FolderType.SUB_FOLDER} />,
                path: ROUTER_PATHS.SUBFOLDER_SETTINGS_BASIC_INFO
              },
              {
                Component: () => <ItemRolesUsersTab />,
                path: ROUTER_PATHS.SUBFOLDER_SETTINGS_ROLES_AND_MEMBERS
              }
            ]
          }
        ]
      },
      {
        Component: () => <Processes />,
        path: ROUTER_PATHS.PROCESSES
      },
      {
        Component: () => <Process />,
        path: ROUTER_PATHS.PROCESS,
        children: [
          {
            Component: () => <ProcessSettingsForm parentRoutePattern={ROUTER_PATHS.PROCESS} />,
            path: ROUTER_PATHS.PROCESS_SETTINGS_FORM
          }
        ]
      },
      {
        Component: () => <CreateProcess />,
        path: ROUTER_PATHS.PROCESS_NEW
      },
      {
        Component: () => <Talents />,
        path: ROUTER_PATHS.TALENTS
      },
      {
        Component: () => <TalentsCreate />,
        path: ROUTER_PATHS.TALENTS_CREATE
      },
      {
        Component: () => <Talent />,
        path: ROUTER_PATHS.TALENT,
        children: [
          {
            Component: () => <TalentTestSession />,
            path: ROUTER_PATHS.TALENT_TEST_SESSION
          }
        ]
      },
      {
        Component: () => <Organizations />,
        path: ROUTER_PATHS.TESTS
      },
      {
        Component: () => <Users />,
        path: ROUTER_PATHS.USERS,
        children: [
          {
            Component: () => <Navigate to={ROUTER_PATHS.USERS_CLIENT} />,
            path: ROUTER_PATHS.USERS
          },
          {
            Component: () => <MidotUsers />,
            path: ROUTER_PATHS.USERS_MIDOT
          },
          {
            Component: () => <ClientUsers />,
            path: ROUTER_PATHS.USERS_CLIENT
          }
        ]
      },
      {
        Component: () => <UserSettings />,
        path: ROUTER_PATHS.USERS_SETTINGS,
        children: [
          {
            Component: () => <UserSettingsBasicInfoTab />,
            path: ROUTER_PATHS.USERS_SETTINGS_BASIC_INFO
          },
          {
            Component: () => <UserSettingsAccessRoles />,
            path: ROUTER_PATHS.USERS_SETTINGS_ACCESS_AND_ROLES
          }
        ]
      },
      {
        Component: () => <Roles />,
        path: ROUTER_PATHS.ROLES
      },
      {
        Component: () => <Navigate to={ROUTER_PATHS.ORGANIZATIONS} />,
        path: ROUTER_PATHS.HOME
      }
    ]
  }
];

export default routes;
